import type { AnalysisRun, RunFailure } from '../model.js';

/**
 * Why a run failed, in Spanish: which step, and whether retrying it can help.
 *
 * The engine does gestures and coherence one way only. When a step cannot be done that way — the
 * transcription fails, the AI cannot split the transcript into ideas or judge the gestures, the configuration asks for a
 * provider or rules that do not exist — the run fails instead of carrying on with a lesser
 * substitute, so that no video comes out worse than another with nothing on screen to say why.
 * That puts the reason on the failure, and every screen that shows one reads it from here.
 *
 * The types are a contract with the engine (`coherence_analysis/errors.py`) and with upstream's
 * automatic retry, which reads the same type to decide. Two kinds:
 *
 * - **configuration**: found before anything runs and fails the same way every time, so it
 *   is never retried; a person fixes the configuration and launches again;
 * - **technical**: may not happen again, so upstream retries it once by itself.
 *
 * A type this build has no words for keeps upstream's code and step. It is still a failure
 * upstream retries — every failure was, before the configuration ones — so it reads as technical.
 */

export const CONFIGURATION_INVALID = 'coherence_configuration_invalid';
export const TRANSCRIPTION_FAILED = 'coherence_transcription_failed';
export const IDEA_SEGMENTATION_FAILED = 'coherence_idea_segmentation_failed';
export const SEMANTIC_CLASSIFICATION_FAILED = 'coherence_semantic_classification_failed';
/**
 * coherence 6.1.0: a run fed by the video's human gesture labels (the semantics control group) found
 * them missing, not marked complete or malformed. Never retried: the labels travel sealed with the
 * run, so a retry would fail the same way; a person finishes labelling the video.
 */
export const GESTURE_LABELS_INVALID = 'coherence_gesture_labels_invalid';
/**
 * coherence 6.2.0: a video's own transcription, made once at upload, was missing or not done when
 * the run started. Never retried by upstream — a retry at once would read the same document — but
 * nothing in the configuration is wrong: relaunched once the transcription is done, it runs.
 */
export const TRANSCRIPTION_MISSING = 'coherence_transcription_missing';

export type RunFailureKind = 'configuration' | 'technical';

export interface RunFailureReason {
  /** Whether this build has words for it. When not, `detail` carries upstream's code and step. */
  known: boolean;
  /** Which step failed, as a sentence. */
  label: string;
  /** The same, short enough for a chip or a table cell. Upstream's code when not `known`. */
  short: string;
  kind: RunFailureKind;
  /** Whether upstream retries a run that failed like this, all else allowing. */
  retryable: boolean;
  /** Why, in upstream's words — English, and the line an operator copies into a ticket. */
  detail: string;
}

interface Wording {
  label: string;
  short: string;
}

/** The configuration errors, by the step the engine refused at. */
const CONFIGURATION_STEPS: Readonly<Record<string, Wording>> = Object.freeze({
  transcription_provider: {
    label: 'La configuración pide un proveedor de transcripción que no existe',
    short: 'Proveedor de transcripción inexistente',
  },
  coherence_ruleset: {
    label: 'Las reglas de veracidad de la configuración no son válidas',
    short: 'Reglas de veracidad no válidas',
  },
});

const ANY_CONFIGURATION: Wording = {
  label: 'La configuración de veracidad no es válida',
  short: 'Configuración no válida',
};

const TECHNICAL: Readonly<Record<string, Wording>> = Object.freeze({
  [TRANSCRIPTION_FAILED]: { label: 'Falló la transcripción', short: 'Falló la transcripción' },
  [IDEA_SEGMENTATION_FAILED]: {
    label: 'La IA no pudo separar las ideas',
    short: 'Falló la separación en ideas',
  },
  [SEMANTIC_CLASSIFICATION_FAILED]: {
    label: 'La IA no pudo juzgar los gestos',
    short: 'Falló el juicio de los gestos',
  },
});

export function runFailureReason(error: RunFailure | null | undefined): RunFailureReason | null {
  if (!error) return null;

  if (error.type === CONFIGURATION_INVALID) {
    const wording = (error.stage && CONFIGURATION_STEPS[error.stage]) || ANY_CONFIGURATION;

    return {
      known: true,
      ...wording,
      kind: 'configuration',
      retryable: false,
      detail: error.message,
    };
  }

  if (error.type === GESTURE_LABELS_INVALID) {
    return {
      known: true,
      label: 'Faltan etiquetas de gestos del vídeo o no están completas',
      short: 'Etiquetas de gestos incompletas',
      kind: 'configuration',
      retryable: false,
      detail: error.message,
    };
  }

  if (error.type === TRANSCRIPTION_MISSING) {
    return {
      known: true,
      label: 'El vídeo no tenía la transcripción lista',
      short: 'Sin transcripción lista',
      kind: 'technical',
      retryable: false,
      detail: error.message,
    };
  }

  const technical = TECHNICAL[error.type];

  if (technical) {
    return { known: true, ...technical, kind: 'technical', retryable: true, detail: error.message };
  }

  const code = error.stage ? `${error.type} · ${error.stage}` : error.type;

  return {
    known: false,
    label: 'La ejecución falló',
    short: error.type,
    kind: 'technical',
    retryable: true,
    detail: `${code}: ${error.message}`,
  };
}

/**
 * The configuration to open so a configuration error can be fixed, by its id.
 *
 * Null for any other failure — the configuration is not what failed — and for a run that did not
 * record which configuration it ran with.
 */
export function configToFix(run: Pick<AnalysisRun, 'error' | 'config'>): number | null {
  // Missing gesture labels are fixed on the video, not in the configuration.
  if (run.error?.type !== CONFIGURATION_INVALID) return null;

  return run.config?.interviewAnalysisConfigId ?? null;
}
